import { Injectable } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { AnimationController } from '@ionic/angular/standalone';
import { TransitionDirection } from '../models/nav-tab.interface';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class TransitionService {
  private currentDirection: TransitionDirection = 'forward';
  private animationController: AnimationController;

  constructor(
    private router: Router,
    animationCtrl: AnimationController
  ) {
    this.animationController = animationCtrl;
    this.setupTransitionListener();
  }

  private setupTransitionListener(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe((event: any) => {
        // Tenta obter a direção do state da navegação
        const navigationState = (event as any).navigation?.extras?.state;
        if (navigationState?.transitionDirection) {
          this.currentDirection = navigationState.transitionDirection;
        }
      });
  }

  /**
   * Define a direção da transição antes da navegação
   */
  setNextTransitionDirection(direction: TransitionDirection): void {
    this.currentDirection = direction;
  }

  /**
   * Cria uma animação de transição baseada na direção especificada
   * A animação é criada dinamicamente usando a direção atual
   */
  createTransitionAnimation(direction?: TransitionDirection) {
    const transitionDirection = direction || this.currentDirection;
    
    return (_baseEl: HTMLElement, opts: any) => {
      const enteringEl = opts.enteringEl;
      const leavingEl = opts.leavingEl;

      const enteringAnimation = this.animationController
        .create()
        .addElement(enteringEl)
        .duration(300)
        .easing('cubic-bezier(0.36,0.66,0.04,1)');

      const leavingAnimation = this.animationController
        .create()
        .addElement(leavingEl)
        .duration(300)
        .easing('cubic-bezier(0.36,0.66,0.04,1)');

      if (transitionDirection === 'left' || transitionDirection === 'back') {
        // Transição LEFT: nova página entra da DIREITA (desliza da direita para esquerda)
        enteringAnimation
          .fromTo('transform', 'translateX(100%)', 'translateX(0%)')
          .fromTo('opacity', '0', '1');
        
        leavingAnimation
          .fromTo('transform', 'translateX(0%)', 'translateX(-100%)')
          .fromTo('opacity', '1', '0.5');
      } else {
        // Transição RIGHT/FORWARD: nova página entra da ESQUERDA (desliza da esquerda para direita)
        enteringAnimation
          .fromTo('transform', 'translateX(-100%)', 'translateX(0%)')
          .fromTo('opacity', '0', '1');
        
        leavingAnimation
          .fromTo('transform', 'translateX(0%)', 'translateX(100%)')
          .fromTo('opacity', '1', '0.5');
      }

      return this.animationController
        .create()
        .addAnimation([enteringAnimation, leavingAnimation]);
    };
  }

  /**
   * Retorna a direção atual da transição
   */
  getCurrentDirection(): TransitionDirection {
    return this.currentDirection;
  }
}

